<template>
  <div role="group" aria-label="Security verification">
    <div ref="container" />
    <p v-if="!siteKey" role="status">
      Verification is temporarily unavailable. Please try again later.
    </p>
    <div v-if="message">
      <p role="status">{{ message }}</p>
      <button v-if="!configurationError" type="button" class="retry" @click="retry">Retry verification</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";

const emit = defineEmits(["token"]);
const siteKey = (process.env.VUE_APP_TURNSTILE_SITE_KEY || "").trim();
const container = ref(null);
const message = ref("");
const configurationError = ref(false);
let cleanup = () => {};
let compact;
let observer;

function load() {
  if (!siteKey) return;
  compact = container.value.clientWidth < 300;
  let active = true;
  let widget;
  let rendered = false;
  const fail = (code) => {
    if (!active) return;
    window.clearTimeout(timer);
    emit("token", "");
    if (String(code) === "110200") {
      configurationError.value = true;
      message.value = "Online verification is unavailable on this website.";
      console.error(`[HB+D Turnstile] 110200: Authorize ${window.location.hostname} in Hostname Management for the widget matching VUE_APP_TURNSTILE_SITE_KEY. Also include this exact hostname in the server's TURNSTILE_ALLOWED_HOSTNAMES.`);
      return;
    }
    if (configurationError.value) return;
    message.value = "Verification could not load. Please retry.";
  };
  const timer = window.setTimeout(fail, 15000);
  const render = () => {
    if (!active || rendered || !window.turnstile || !container.value) return;
    window.clearTimeout(timer);
    try {
      widget = window.turnstile.render(container.value, {
        sitekey: siteKey,
        action: "hbd_contact",
        theme: "dark",
        size: compact ? "compact" : "flexible",
        callback: (token) => {
          if (!active) return;
          emit("token", token);
          configurationError.value = false;
          message.value = "";
        },
        "expired-callback": () => {
          if (!active) return;
          emit("token", "");
          message.value = "Verification expired. Please verify again.";
        },
        "error-callback": fail,
      });
      rendered = true;
    } catch { fail(); }
  };
  let script = document.getElementById("hbd-turnstile");
  const scriptFailed = () => { script?.remove(); fail(); };
  if (window.turnstile) render();
  else {
    const fresh = !script;
    if (!script) {
      script = document.createElement("script");
      script.id = "hbd-turnstile";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
    }
    script.addEventListener("load", render);
    script.addEventListener("error", scriptFailed);
    if (fresh) document.head.appendChild(script);
  }
  cleanup = () => {
    active = false;
    window.clearTimeout(timer);
    script?.removeEventListener("load", render);
    script?.removeEventListener("error", scriptFailed);
    if (widget !== undefined) window.turnstile?.remove(widget);
  };
}

function retry() {
  if (configurationError.value) return;
  cleanup();
  // A timed-out script may never emit an error; retry must start a fresh request.
  if (!window.turnstile) document.getElementById("hbd-turnstile")?.remove();
  emit("token", "");
  message.value = "";
  load();
}
onMounted(() => {
  load();
  if (!siteKey) return;
  observer = new ResizeObserver(() => {
    if ((container.value.clientWidth < 300) !== compact) retry();
  });
  observer.observe(container.value);
});
onBeforeUnmount(() => {
  observer?.disconnect();
  cleanup();
});
</script>

<style scoped>
p { font-size: 0.875em; line-height: 1.5; }
.retry { text-decoration: underline; text-underline-offset: 0.2em; }
.retry { padding: 0.5em 0; }
</style>
